import { useState } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import {
  AssignmentPlan,
  AssignmentSnapshot,
  canClearEmployee,
  PlannerSlot,
  slotDay,
  slotUnavailableReason,
} from "@/components/appointmentRequest/assignmentPlanner/assignmentPlan";
import PlannerSlotTime from "@/components/appointmentRequest/assignmentPlanner/plannerSlotTime";
import { plannerRowStyle } from "@/components/appointmentRequest/assignmentPlanner/plannerLayout";
import PlannerIcon from "@/components/appointmentRequest/assignmentPlanner/plannerIcon";
import PlannerToggle from "@/components/appointmentRequest/assignmentPlanner/plannerToggle";
import styles from "@/components/appointmentRequest/assignmentPlanner/planner.module.css";

export interface PlannerSlotsProps {
  snapshot: AssignmentSnapshot;
  plan: AssignmentPlan;
  day: string;
  complete?: boolean;
  employeeSelection?: { id: string; onSelect: (slotId: string) => void };
  onOpen?: (slot: PlannerSlot, anchor: HTMLElement) => void;
  onClear?: (employeeId: string) => void;
}
export default function PlannerSlots({
  snapshot,
  plan,
  day,
  complete,
  employeeSelection,
  onOpen,
  onClear,
}: PlannerSlotsProps) {
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(true);
  const slots = snapshot.slots
    .filter((slot) => slotDay(slot) === day)
    .sort(
      (left, right) =>
        dayjs(left.start_date).valueOf() - dayjs(right.start_date).valueOf() ||
        left.id.localeCompare(right.id),
    );
  const employeesBySlot = new Map(
    snapshot.employees
      .filter((employee) => plan[employee.id])
      .map((employee) => [plan[employee.id], employee]),
  );
  const occupied = (slot: PlannerSlot) =>
    Boolean(
      employeesBySlot.has(slot.id) || (slot.occupied && !slot.employee_id),
    );
  const visible = slots.filter(
    (slot) => showAll || (!occupied(slot) && !slotUnavailableReason(slot)),
  );
  return (
    <section
      className={`${styles.column} ${styles.slotsColumn}`}
      style={plannerRowStyle}
      aria-label={t("planner.slots")}
    >
      <div className={styles.toolbar}>
        <div className={styles.slotHeading}>
          <strong>{dayjs(day).format("DD.MM.YYYY")}</strong>
          <span className={styles.hint}>
            (
            {t("planner.occupied_count", {
              occupied: slots.filter(occupied).length,
              total: slots.length,
            })}
            )
          </span>
        </div>
        <PlannerToggle
          all={showAll}
          onChange={setShowAll}
          freeLabel={t("planner.empty_only")}
        />
      </div>
      <div className={`${styles.panel} ${styles.slotPanel}`}>
        <div className={styles.slotGrid} role="list">
          {visible.map((slot) => {
            const employee = employeesBySlot.get(slot.id);
            const reason = slotUnavailableReason(slot);
            const blocked = reason !== null;
            const current =
              employeeSelection && employee?.id === employeeSelection.id;
            const actionDisabled =
              blocked ||
              Boolean(current) ||
              Boolean(employee && !canClearEmployee(employee));
            const open = (anchor: HTMLElement) => {
              if (actionDisabled) return;
              if (employeeSelection) employeeSelection.onSelect(slot.id);
              else onOpen?.(slot, anchor);
            };
            return (
              <div
                key={slot.id}
                role="listitem"
                data-planner-slot={slot.id}
                data-unavailable-reason={reason ?? undefined}
                title={reason ? t(`planner.slot_${reason}`) : undefined}
                className={`${styles.slot} ${occupied(slot) ? styles.assigned : complete ? styles.unused : ""} ${current ? styles.currentSlot : ""}`}
                onClick={(event) => {
                  if (event.target === event.currentTarget)
                    open(event.currentTarget);
                }}
              >
                <button
                  type="button"
                  className={styles.slotSelect}
                  disabled={actionDisabled}
                  aria-label={`${t(current ? "planner.current" : employee && !employeeSelection ? "planner.edit" : "planner.assign")} ${dayjs(slot.start_date).format("HH:mm")}`}
                  onClick={(event) => open(event.currentTarget)}
                >
                  <PlannerSlotTime slot={slot} />
                  <span className={styles.name} title={employee?.name}>
                    {employee?.name ??
                      (reason ? t(`planner.slot_${reason}`) : null)}
                  </span>
                </button>
                {employee && !employeeSelection && (
                  <button
                    type="button"
                    className={`${styles.icon} ${styles.slotClear}`}
                    disabled={!canClearEmployee(employee)}
                    aria-label={`${t("planner.clear")} ${employee.name}`}
                    onClick={() => onClear?.(employee.id)}
                  >
                    <PlannerIcon name="x" />
                  </button>
                )}
              </div>
            );
          })}
          {!visible.length && (
            <p className={styles.empty}>
              {t(showAll ? "planner.no_slots" : "planner.no_free_slots")}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
